import {
    clearKeyword,
    clearSelection,
    setKeyword,
} from '../reducers';

import {
    selectionSelector,
    selectVisibility,
    visibilityStateSelector,
} from '../selectors';

import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
// Do not hand-roll this; §3a says so explicitly and gives the reason.
import {useTableSort} from '@flexinet/ui-components';

import {DomainName} from '../types';
import {
    useAppDispatch,
    useAppSelector
} from '../../../hooks/useTypedReactRedux';

import {
    useDeleteDomainNameMutation,
    useDomainNamesQuery
} from './useDomainNamesQuery';
import {useDomainNamesTableColumns} from './useDomainNamesTableColumns';

// FNP-1282 §3a — `created` DESCENDING, newest first.
//
// ⚠️ THE KEY MUST BE IMMUTABLE AND NON-NULL, and `created` only became a legal
// choice on 2026-08-30. FNP-1281 made `DomainName.created` `AWSDateTime!` in the
// schema and `created: string` in types.ts, after measuring 15 of 15 PROD rows
// carrying it and reading the deployed `createDomainName` resolver, which stamps
// `created: now` unconditionally. Before that this sort was BLOCKED: a nullable
// key degrades silently to insertion order for every row that lacks it.
//
// ⚠️ NEVER `updated` — it moves under the reader, so the list reorders itself
// while someone is looking at it. `purchaseDate` and `expirationDate` are both
// still nullable and sparsely populated (1 of 15 and 0 of 15), so neither is a
// candidate either.
//
// The API lists on `record-type-created-index` newest-first, so this default
// agrees with the server and the first paint does not reshuffle.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<DomainName> = {
    columnAccessor: 'created',
    direction: 'desc',
};

// FNP-942: records come from React Query; selection, paging and keyword stay in
// Redux. The return shape is unchanged so the table, buttons, inputs and the
// dashboard widget that consume this hook did not have to change.
export const useDomainNames = (registrarId?: string) => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));
    const visibility = useAppSelector((state) => visibilityStateSelector(state));

    const query = useDomainNamesQuery();

    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isRefetching,
    } = query;

    const deleteMutation = useDeleteDomainNameMutation();

    // FNP-1282: flatten the pages useInfiniteQuery accumulated. The `'items' in
    // page` guard is not defensive noise — a page object that came back partial
    // would otherwise make this flatMap throw inside a render.
    const records: DomainName[] = (data?.pages ?? [])
        .flatMap(page => (page && 'items' in page) ? (page.items ?? []) : [])
        .filter(Boolean);

    const {
        items,
        visibleItems,
        keyword,
        visibleCount,
    } = selectVisibility(visibility, records, registrarId);

    const {columns} = useDomainNamesTableColumns();

    // FNP-1282 §3a — part THREE of the sorting contract: the comparator applied
    // to the records. The table does not sort for you.
    //
    // ⚠️ SORT STATE IS COMPONENT-LEVEL, held by `useTableSort`'s own useState —
    // NOT the Redux visibility slice. Sort order is how ONE operator is looking
    // at the table right now; the keyword and the filters are shared across
    // sibling components, which is why those stay in the store and this does
    // not.
    //
    // ⚠️ SORTED AFTER the keyword narrows, so the order is over the rows on
    // screen. And the whole thing is CLIENT-SIDE, over the pages fetched so far
    // — a property of token pagination, not an oversight. Sorting page one does
    // not sort the dataset, which is why the footer reports a LOADED count
    // rather than a total it cannot know.
    const {
        sortStatus,
        setSortStatus,
        sorted,
    } = useTableSort<DomainName>(visibleItems, DEFAULT_SORT_STATUS);

    // FETCH — loads the NEXT page under token pagination; falls back to a
    // refetch when everything the server has is already loaded, which is what
    // the Reload button and the mount effects want.
    const fetchRecords = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
            return;
        }
        refetch();
    };

    const deleteRecords = () => {
        if (Object.keys(selection).length > 0) {
            for (const key in selection) {
                deleteMutation.mutate(key);
            }
            // Clear the checkboxes once the deletions are issued — otherwise the
            // selection keeps naming records that no longer exist.
            dispatch(clearSelection());
        }
    };


    // FILTERING AND PAGING
    const handleFilterByKeyword = (keyword: string) => {
        if (keyword !== '') {
            dispatch(setKeyword(keyword));
        } else {
            dispatch(clearKeyword());
        }
    };

    // ⚠️ NO `handlePageChange` / `handleItemsPerPageChange`. FNP-1282 removed
    // both with the offset slice they drove: under token pagination there is no
    // page to jump to, no total to divide, and no page size for a user to
    // choose — the page size is a SERVER concern, fixed at
    // DomainNamesService.DEFAULT_PAGE_SIZE. Nothing outside this hook called
    // either one; `RecordsTable` renders `TableSearchForm`, not the
    // `SimpleTableBatchAction` that used to demand them.

    // BATCH ACTIONS
    const handleBatchActionCancel = () => {
        dispatch(clearSelection());
    };

    return {
        items,
        // ⚠️ THE SORTED SET, not the raw filtered one. `visibleItems` is
        // returned under its established name so the table and the other
        // consumers keep working, but what they get is now ordered.
        visibleItems: sorted,
        sortStatus,
        setSortStatus,
        loading: isLoading,
        // FNP-1282: surfaced so the table can tell a FAILED load apart from an
        // empty one. Previously dropped at this boundary, which is why three
        // different states all rendered as the same empty table.
        isError,
        error,
        isFetchingNextPage,
        isRefetching,
        // Derived from hasNextPage, never from items.length — a sentinel, since
        // the footer only needs it to be truthy or null.
        nextToken: hasNextPage ? 'has-more' : null,
        reload: () => refetch(),
        keyword,
        visibleCount,
        columns,
        fetchRecords,
        deleteRecords,
        handleFilterByKeyword,
        handleBatchActionCancel,
    };
};
